import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Table } from "ui-kit/Table";
import { Routes } from "constants/routes";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useLabels } from "./hooks/useLabels";
import { useCountries } from "./hooks/useCountries";
import { getColumns } from "./columns";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { CreateLabelModal } from "./CreateLabelModal";
import { DeleteLabelModal } from "./DeleteLabelModal";
import IntlStore from "stories/IntlStore";

export const Labels = () => {
  const [search, setSearch] = useState<string>('');
  const [first, setFirst] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(30);
  const [isOpenCreateLabelModal, setIsOpenCreateLabelModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { labels, total, createLabel, checkLabel, deleteLabel, resetCheckLabel, checkingStatus, checking, creating, deleting, getLabels } = useLabels(currentPage, pageCount);
  const searchedLabels = search ? labels.filter(label => label.name.includes(search)) : labels;
  const toast = useRef<any>(null);
  const navigate = useNavigate();
  const { countries } = useCountries();

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.rows * event.page);
    setPageCount(event.rows);
  };

  const onCreate = () => setIsOpenCreateLabelModal(true);

  const onCreateLabel = async (name: string) => {
    try {
      const firstCountry = countries.length ? countries[0] : { id: 1 };
      await createLabel(name, String(firstCountry.id));
      
      setIsOpenCreateLabelModal(false);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.LabelAddedSuccessfully",
          defaultMessage: "Label added successfully"
        }),
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  };

  const onDeleteLabel = async () => {
    try {
      if (!deleteId) return;
      await deleteLabel(deleteId);
      setDeleteId(null);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.LabelRemovedSuccessfully",
          defaultMessage: "Label removed successfully"
        })
      });
    } catch (e: any) {
      
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.response.data.message
      });
      setDeleteId(null);
    }
  };

  const onEdit = (id: string) => navigate(Routes.LabelsDetails.replace(':id', id));

  const onDelete = (id: string) => setDeleteId(id);

  return (
    <Container>
      <FilterContainer>
        <SearchInput
          name="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={
            IntlStore.intl.formatMessage({
              id: "Labels.SearchLabels",
              defaultMessage: "Search by labels"
            })
          }
        />
        <CreateNewLabelButton onClick={onCreate}>
          <svg width="85" height="18" viewBox="0 0 85 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.7544 14.1523C18.1392 14.1523 16.4951 12.356 16.4951 9.41699C16.4951 6.49707 18.1519 4.68799 20.7544 4.68799C22.8809 4.68799 24.4995 6.01465 24.7026 7.98242H23.0903C22.8745 6.8335 21.9414 6.07812 20.7544 6.07812C19.1802 6.07812 18.1709 7.37305 18.1709 9.41699C18.1709 11.4736 19.1738 12.7622 20.7607 12.7622C21.9731 12.7622 22.8428 12.1084 23.0967 11.0039H24.7026C24.417 12.9653 22.9189 14.1523 20.7544 14.1523ZM25.7881 14V7.27148H27.3052V8.4458H27.3369C27.5654 7.62695 28.1558 7.15723 28.9492 7.15723C29.1523 7.15723 29.3301 7.19531 29.4443 7.2207V8.64893C29.311 8.59814 29.0698 8.56006 28.8032 8.56006C27.9082 8.56006 27.3687 9.1377 27.3687 10.0835V14H25.7881ZM32.9038 8.35693C32.0151 8.35693 31.374 9.03613 31.3105 9.97559H34.4526C34.4209 9.02344 33.8115 8.35693 32.9038 8.35693ZM34.459 11.9878H35.938C35.7349 13.2573 34.5542 14.1333 32.9609 14.1333C30.9424 14.1333 29.7236 12.8066 29.7236 10.6611C29.7236 8.53467 30.9551 7.13184 32.8975 7.13184C34.8081 7.13184 36.0015 8.4585 36.0015 10.5024V11.0166H31.2979V11.1118C31.2979 12.1909 31.958 12.9146 32.9927 12.9146C33.729 12.9146 34.2876 12.5464 34.459 11.9878ZM39.48 12.9146C40.375 12.9146 41.0923 12.3179 41.0923 11.5054V10.9912L39.5371 11.0864C38.7754 11.1372 38.3374 11.48 38.3374 12.0132C38.3374 12.5654 38.7944 12.9146 39.48 12.9146ZM39.0039 14.1079C37.7026 14.1079 36.7568 13.2891 36.7568 12.0767C36.7568 10.8833 37.6772 10.166 39.3149 10.0645L41.0923 9.96289V9.48047C41.0923 8.77588 40.6162 8.36963 39.8354 8.36963C39.0864 8.36963 38.6104 8.73145 38.5088 9.29004H37.0425C37.1123 8.0332 38.1851 7.13184 39.8989 7.13184C41.5684 7.13184 42.6538 8.01416 42.6538 9.36621V14H41.1304V12.9272H41.0986C40.7114 13.6572 39.8672 14.1079 39.0039 14.1079ZM44.3677 5.69727H45.9482V7.27148H47.2178V8.49023H45.9482V11.9878C45.9482 12.54 46.2021 12.7939 46.7607 12.7939C46.9067 12.7939 47.1099 12.7812 47.2114 12.7622V13.9683C47.04 14.0063 46.729 14.0317 46.3926 14.0317C44.939 14.0317 44.3677 13.5176 44.3677 12.21V8.49023H43.4028V7.27148H44.3677V5.69727ZM51.0708 8.35693C50.1821 8.35693 49.541 9.03613 49.4775 9.97559H52.6196C52.5879 9.02344 51.9785 8.35693 51.0708 8.35693ZM52.626 11.9878H54.105C53.9019 13.2573 52.7212 14.1333 51.1279 14.1333C49.1094 14.1333 47.8906 12.8066 47.8906 10.6611C47.8906 8.53467 49.1221 7.13184 51.0645 7.13184C52.9751 7.13184 54.1685 8.4585 54.1685 10.5024V11.0166H49.4648V11.1118C49.4648 12.1909 50.125 12.9146 51.1597 12.9146C51.896 12.9146 52.4546 12.5464 52.626 11.9878ZM57.8374 14V4.84033H59.418V14H57.8374ZM63.2202 12.9146C64.1152 12.9146 64.8325 12.3179 64.8325 11.5054V10.9912L63.2773 11.0864C62.5156 11.1372 62.0776 11.48 62.0776 12.0132C62.0776 12.5654 62.5347 12.9146 63.2202 12.9146ZM62.7441 14.1079C61.4429 14.1079 60.4971 13.2891 60.4971 12.0767C60.4971 10.8833 61.4175 10.166 63.0552 10.0645L64.8325 9.96289V9.48047C64.8325 8.77588 64.3564 8.36963 63.5757 8.36963C62.8267 8.36963 62.3506 8.73145 62.249 9.29004H60.7827C60.8525 8.0332 61.9253 7.13184 63.6392 7.13184C65.3086 7.13184 66.394 8.01416 66.394 9.36621V14H64.8706V12.9272H64.8389C64.4517 13.6572 63.6074 14.1079 62.7441 14.1079ZM71.4341 14.1079C70.4438 14.1079 69.6885 13.6318 69.2949 12.8511H69.2632V14H67.7144V4.84033H69.2949V8.42676H69.333C69.7139 7.63965 70.4819 7.15723 71.4341 7.15723C73.1289 7.15723 74.208 8.48389 74.208 10.6357C74.208 12.7876 73.1353 14.1079 71.4341 14.1079ZM70.9453 8.4585C69.9678 8.4585 69.2886 9.32812 69.2886 10.6357C69.2886 11.9561 69.9678 12.813 70.9453 12.813C71.9609 12.813 72.5894 11.9751 72.5894 10.6357C72.5894 9.30273 71.9546 8.4585 70.9453 8.4585ZM78.1372 8.35693C77.2485 8.35693 76.6074 9.03613 76.5439 9.97559H79.686C79.6543 9.02344 79.0449 8.35693 78.1372 8.35693ZM79.6924 11.9878H81.1714C80.9683 13.2573 79.7876 14.1333 78.1943 14.1333C76.1758 14.1333 74.957 12.8066 74.957 10.6611C74.957 8.53467 76.1885 7.13184 78.1309 7.13184C80.0415 7.13184 81.2349 8.4585 81.2349 10.5024V11.0166H76.5312V11.1118C76.5312 12.1909 77.1914 12.9146 78.2261 12.9146C78.9624 12.9146 79.521 12.5464 79.6924 11.9878ZM82.3013 14V4.84033H83.8818V14H82.3013Z" fill="#6777FF"/>
            <circle cx="6" cy="9" r="6" fill="#6777FF"/>
            <path d="M6.70605 9.73438V11.9023H5.60742V9.73438H3.58105V8.65039H5.60742V6.5166H6.70605V8.65039H8.7373V9.73438H6.70605Z" fill="white"/>
          </svg>
        </CreateNewLabelButton>
      </FilterContainer>
      <Table
        total={total}
        pageCount={pageCount}
        currentPage={first}
        columns={getColumns({ onEdit, onDelete })}
        data={searchedLabels}
        onPageChange={onPageChange}
      />
      <CreateLabelModal
        status={checkingStatus}
        checking={checking}
        creating={creating}
        onCreateLabel={onCreateLabel}
        onCheckLabel={checkLabel}
        onResetCheckLabel={resetCheckLabel}
        onHide={() => setIsOpenCreateLabelModal(false)}
        isOpen={isOpenCreateLabelModal}
      />
      <DeleteLabelModal
        deleting={deleting}
        onDeleteLabel={onDeleteLabel}
        onHide={() => setDeleteId(null)}
        isOpen={!!deleteId}
      />
      <Toast ref={toast} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 28px 20px 18px;
`;

const SearchInput = styled(Input)`
  height: 43px;
  width: 333px;
  overflow: hidden;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CreateNewLabelButton = styled.div`
  cursor: pointer;
`;
